import { snakeCase } from "lodash";
import Vue from "vue";

import {
  MenuBaseFields,
  MenuItem,
  MenuItems,
  MenuItemTypeEnum,
  TemplateMenuConfigBaseFields,
  TemplateMenuConfigItem,
  TemplateMenuConfigItems
} from ".";
import { AnyValue } from "../../../shared/types/AnyValue";
import getAnyValue from "../../../shared/utils/getAnyValue";
import TemplateContext from "../includes/TemplateContext";
import { findRouteByNameAndGetParentGuids, getMenuItemAlias } from "./util";

let templateContext: TemplateContext | null = null;
let activeKeysSidebarDropdown: Array<string> = []

async function resolveListOfAnyValue<T>(items: Array<AnyValue<T>>) {
  return Promise.all(items.map(b => getAnyValue(b, templateContext)))
}

async function processBaseFields<T extends TemplateMenuConfigBaseFields>(item: T): Promise<MenuBaseFields> {
  return Promise.all([
    item.class ? resolveListOfAnyValue(item.class) : undefined,
    item.tags ? resolveListOfAnyValue(item.tags) : undefined
  ])
    .then((res) => {
      return {
        alias: item?.alias ?? undefined,
        class: res[0],
        tags: res[1],
        icon: item?.icon ?? undefined,
      }
    })
}

async function getVisibleItems(items: Array<TemplateMenuConfigItem>): Promise<Array<TemplateMenuConfigItem>> {
  const visibleItems: Array<TemplateMenuConfigItem> = []

  for (const item of items) {
    if (item.isVisible) {
      const results = await resolveListOfAnyValue(item.isVisible)
      if (results.every(r => r)) {
        if (item.type === MenuItemTypeEnum.Dropdown) {
          item.children = await getVisibleItems(resolveTemplateMenuItems(item.children))

          visibleItems.push(item)
        } else {
          visibleItems.push(item)
        }
      }
    } else {
      visibleItems.push(item)
    }
  }

  return visibleItems
}

// eslint-disable-next-line sonarjs/cognitive-complexity
async function getSidebarItem(item: TemplateMenuConfigItem): Promise<MenuItem> {
  const baseFields = await processBaseFields(item)

  if (item.type === MenuItemTypeEnum.Route) {
    const route = await getAnyValue(item.route)
    const routeNameSc = snakeCase(route.name)

    return {
      type: MenuItemTypeEnum.Route,
      ...baseFields,
      alias: getMenuItemAlias(routeNameSc),
      route,
    }
  }

  if (item.type === MenuItemTypeEnum.Dropdown) {
    const key = findRouteByNameAndGetParentGuids(item, templateContext?.route?.name ?? '')

    if (key) {
      activeKeysSidebarDropdown.push(...key)
    }

    const selected = item.selected
      ? await resolveListOfAnyValue(item.selected)
      : undefined

    let children: Array<MenuItem> = []

    if (item.children) {
      const items = resolveTemplateMenuItems(item.children)

      children = await Promise.all(items.map(c => getSidebarItem(c)))
    }

    return {
      type: MenuItemTypeEnum.Dropdown,
      ...baseFields,
      alias: item.alias,
      guid: item.guid,
      children: children,
      selected: Array.isArray(selected) ? [ selected.every(b => b) ] : undefined
    }
  }

  if (item.type === MenuItemTypeEnum.DynamicHeader) {
    return {
      type: MenuItemTypeEnum.DynamicHeader,
      ...baseFields,
      title: await getAnyValue(item.title, templateContext),
      img: await getAnyValue(item.img, templateContext),
      route: item?.route ?? undefined,
    }
  }

  if (item.type === MenuItemTypeEnum.Divider) {
    return {
      type: MenuItemTypeEnum.Divider,
      ...baseFields
    }
  }

  if (item.type === MenuItemTypeEnum.Link) {
    return {
      type: MenuItemTypeEnum.Link,
      ...baseFields,
      href: item.href ? await getAnyValue(item.href, templateContext) : undefined,
      route: item.route?? undefined,
      onClick: item.onClick ?? undefined,
    }
  }

  if (item.type === MenuItemTypeEnum.Button) {
    return {
      type: MenuItemTypeEnum.Button,
      ...baseFields,
      antType: item.antType,
      antSize: item.antSize,
      href: item.href ? await getAnyValue(item.href, templateContext) : undefined,
      route: item.route?? undefined,
      onClick: item.onClick ?? undefined,
    }
  }

  if (item.type === MenuItemTypeEnum.Component) {
    return {
      type: MenuItemTypeEnum.Component,
      ...baseFields,
      component: item.component,
    }
  }

  throw new Error(`Unknown sidebar item, got: ${ item }`)
}

async function getSidebarItems(items: Array<TemplateMenuConfigItem>): Promise<MenuItems> {
  return Promise.all(items.map(i => getSidebarItem(i)))
}

export function resolveTemplateMenuItems(menuItems: TemplateMenuConfigItems) {
  return menuItems.map(i => {
    if (typeof i === 'function') {
      return i()
    }

    return i
  })
    .flat()
}

export default async function newProcessConfigMenuItems(context: Vue, menuItems: TemplateMenuConfigItems): Promise<{
  items: MenuItems,
  activeDropdownKeys: Array<string>
}> {
  activeKeysSidebarDropdown = []
  templateContext = new TemplateContext(context.$route)

  const visibleItems = await getVisibleItems(resolveTemplateMenuItems(menuItems))

  return {
    items: await getSidebarItems(visibleItems),
    activeDropdownKeys: activeKeysSidebarDropdown
  }
}
